// Utility: sanitize template HTML before token detection/rendering
// - Replaces Tailwind CDN (JS compiler) with pre-compiled inline CSS
// - Normalizes image tokens to {{photoURL}}
// - Standardizes <img src> Mustache tokens to use {{photoURL}} and adds alt
// - Removes excessive emojis for a more professional look

// Import pre-compiled Tailwind CSS as a raw string (bundled into JS — no fetch needed)
import TAILWIND_CSS from './tw-templates.css?raw';

/**
 * Remove most emoji characters from a string
 * Covers common ranges; safe no-op for plain text
 */
const stripEmojis = (html) => {
  let out = html;
  // Remove common emoji blocks via sequential simple ranges to avoid complex character classes
  out = out.replace(/[\u{1F300}-\u{1F5FF}]/gu, ''); // Misc Symbols and Pictographs
  out = out.replace(/[\u{1F600}-\u{1F64F}]/gu, ''); // Emoticons
  out = out.replace(/[\u{1F680}-\u{1F6FF}]/gu, ''); // Transport and Map Symbols
  out = out.replace(/[\u{1F700}-\u{1F77F}]/gu, ''); // Alchemical Symbols (rare)
  out = out.replace(/[\u{1F780}-\u{1F7FF}]/gu, ''); // Geometric Shapes Extended
  out = out.replace(/[\u{1F800}-\u{1F8FF}]/gu, ''); // Supplemental Arrows-C
  out = out.replace(/[\u{1F900}-\u{1F9FF}]/gu, ''); // Supplemental Symbols and Pictographs
  out = out.replace(/[\u{1FA00}-\u{1FAFF}]/gu, ''); // Symbols and Pictographs Extended-A
  out = out.replace(/[\u{2600}-\u{26FF}]/gu, ''); // Miscellaneous Symbols
  out = out.replace(/[\u{2700}-\u{27BF}]/gu, ''); // Dingbats
  out = out.replace(/[\u{FE0F}]/gu, ''); // Variation Selector-16
  return out;
};

/**
 * Normalize image Mustache tokens across templates
 * Any of {{photo}}, {{profilePhoto}}, {{avatar}}, {{headshot}}, {{image}}, {{picture}} -> {{photoURL}}
 */
const normalizeImageTokens = (html) => {
  const tokenRegex = /\{\{\s*(photoURL|photo|profilePhoto|avatar|headshot|image|picture)\s*\}\}/g;
  return html.replace(tokenRegex, '{{photoURL}}');
};

/**
 * Standardize <img src="{{token}}"> to use {{photoURL}} and include alt attribute
 */
const standardizeImgTags = (html) => {
  return html.replace(/<img([^>]*?)src=['"]\s*\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}\s*['"]([^>]*?)>/gi,
    (match, before, token, after) => {
      // Preserve existing class/style attributes from before/after
      const hasAlt = /\salt\s*=/.test(match);
      const altAttr = hasAlt ? '' : ' alt="Profile Photo"';
      return `<img${before}src="{{photoURL}}"${after}${altAttr}>`;
    }
  );
};

/**
 * Replace Tailwind CDN Play (JavaScript compiler) with INLINED pre-compiled CSS.
 * 
 * The CSS is imported as a raw string at build time (via Vite's ?raw),
 * so it's bundled directly into JavaScript — ZERO network fetch needed.
 * 
 * This guarantees the CSS is ALWAYS available when sanitizeTemplateHTML runs,
 * regardless of network conditions, timing, or iframe origin restrictions.
 */
const replaceTailwindCDNWithCSS = (html) => {
  // Check if template uses Tailwind CDN
  const cdnPattern = /<script[^>]*src=["']https?:\/\/cdn\.tailwindcss\.com[^"']*["'][^>]*>\s*<\/script>/i;
  if (!cdnPattern.test(html)) return html; // No Tailwind CDN, nothing to fix

  let result = html;

  // 1. Remove the Tailwind CDN script tag
  result = result.replace(cdnPattern, '');

  // 2. Remove tailwind.config scripts (not needed — CSS is pre-compiled)
  result = result.replace(
    /<script[^>]*>[\s\S]*?tailwind\.config[\s\S]*?<\/script>/gi,
    ''
  );

  // 3. Remove console.warn suppression scripts (no CDN = no warnings)
  result = result.replace(
    /<script[^>]*>[\s\S]*?cdn\.tailwindcss\.com[\s\S]*?<\/script>/gi,
    ''
  );

  // 4. Inject the pre-compiled CSS INLINE (direct from JS bundle — always available)
  const styleTag = `<style id="tw-precompiled">${TAILWIND_CSS}</style>`;
  const headPattern = /(<head[^>]*>(\s*<meta[^>]*charset[^>]*>)?)/i;
  const headMatch = result.match(headPattern);

  if (headMatch) {
    const insertPoint = headMatch.index + headMatch[0].length;
    result = result.slice(0, insertPoint) + '\n' + styleTag + '\n' + result.slice(insertPoint);
  } else {
    result = styleTag + '\n' + result;
  }

  return result;
};

export const sanitizeTemplateHTML = (html) => {
  if (!html || typeof html !== 'string') return html;
  let cleaned = html;
  cleaned = replaceTailwindCDNWithCSS(cleaned);
  cleaned = normalizeImageTokens(cleaned);
  cleaned = standardizeImgTags(cleaned);
  cleaned = stripEmojis(cleaned);
  return cleaned;
};

export default sanitizeTemplateHTML;